import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import LinkIcon from "@mui/icons-material/Link";
import {useEffect, useMemo, useRef, useState} from "react";
import {useParams} from "react-router-dom";
import {useCurrentProfile} from "../../../../../ContextWrapper";
import LinkPlaidButton from "../../../../../components/plaid/linkPlaidMUI";
import {
  BankConnectedAccountsTableOnly,
  BankConnectedAccountsTableOnlySkeleton,
} from "../../../../../components/welcomeSetup/elements/BankConnectedAccountsStep";
import {
  deletePlaidItem,
  fakePlaidImport,
  fetchPlaiditems,
  flipBankAccountSignConvention,
  updateBankAccount,
  updatePlaidItem,
} from "../../../../../api/plaid";
import {
  csvAmountSignConventionFromTypeKey,
  deriveAccountTypeKeyFromApi,
  plaidTypeSubtypeFromTypeKey,
} from "../../../../../utils/bankAccountTypeUtils";

const ICON_COL_WIDTH_PX = 56;
const EVEN_COL_WIDTH = `calc((100% - ${ICON_COL_WIDTH_PX}px) / 3)`;
const evenlySpacedTableSx = {
  "& .MuiTable-root": {
    tableLayout: "fixed",
  },
  "& .MuiTableHead-root .MuiTableRow-root > .MuiTableCell-root:nth-of-type(1), & .MuiTableBody-root .MuiTableRow-root > .MuiTableCell-root:nth-of-type(1)":
    {
      width: `${ICON_COL_WIDTH_PX}px`,
    },
  "& .MuiTableHead-root .MuiTableRow-root > .MuiTableCell-root:nth-of-type(2), & .MuiTableBody-root .MuiTableRow-root > .MuiTableCell-root:nth-of-type(2), & .MuiTableHead-root .MuiTableRow-root > .MuiTableCell-root:nth-of-type(3), & .MuiTableBody-root .MuiTableRow-root > .MuiTableCell-root:nth-of-type(3), & .MuiTableHead-root .MuiTableRow-root > .MuiTableCell-root:nth-of-type(4), & .MuiTableBody-root .MuiTableRow-root > .MuiTableCell-root:nth-of-type(4)":
    {
      width: EVEN_COL_WIDTH,
      minWidth: 0,
    },
};

const normalizeConvention = (value) => {
  const raw = (value || "").toString().trim().toLowerCase();
  return raw === "lucro" ? "lucro" : "plaid";
};

const conventionLabel = (value) => {
  const v = normalizeConvention(value);
  return v === "lucro" ? "Lucro" : "Plaid";
};

export default function BankConnections() {
  const { profile } = useCurrentProfile();
  const { customer_id: routeCustomerId } = useParams();

  const [storedCustomerId, setStoredCustomerId] = useState(() =>
    sessionStorage.getItem("selectedCustomerId")
  );

  useEffect(() => {
    const syncFromStorage = () => {
      setStoredCustomerId(sessionStorage.getItem("selectedCustomerId"));
    };

    window.addEventListener("selectedCustomerId-changed", syncFromStorage);
    return () =>
      window.removeEventListener("selectedCustomerId-changed", syncFromStorage);
  }, []);

  const fallbackCustomerId = useMemo(() => {
    const customers = Array.isArray(profile?.customers) ? profile.customers : [];
    const active = customers.find((c) => c?.is_active) || customers[0];
    return active?.id || null;
  }, [profile?.customers]);

  const customerId = routeCustomerId || storedCustomerId || fallbackCustomerId || null;

  // Bank Connections screen should only show the connected accounts table.
  // The "Account Setup" header/copy is shown in `practiceSetupPart2` instead.
  const ConnectedAccountsStepComponent = BankConnectedAccountsTableOnly;
  const ConnectedAccountsSkeletonComponent = BankConnectedAccountsTableOnlySkeleton;

  const [plaidItems, setPlaidItems] = useState([]);
  const [checkingPlaid, setCheckingPlaid] = useState(false);
  const [plaidLinking, setPlaidLinking] = useState(false);
  const [creatingCsvAccount, setCreatingCsvAccount] = useState(false);

  const [conventionPendingByAccountId, setConventionPendingByAccountId] =
    useState({});
  const [conventionDialogOpen, setConventionDialogOpen] = useState(false);
  const [conventionDialogRow, setConventionDialogRow] = useState(null);
  const [conventionDialogValue, setConventionDialogValue] = useState("plaid");

  const [draftAccountSetupRows, setDraftAccountSetupRows] = useState([]);
  const draftRowsRef = useRef([]);
  const seededFromExistingRef = useRef(false);

  const totalCount = Array.isArray(draftAccountSetupRows)
    ? draftAccountSetupRows.length
    : 0;
  const connectedCount = Array.isArray(draftAccountSetupRows)
    ? draftAccountSetupRows.filter((r) => r?.isConnected).length
    : 0;

  const [pendingDraftConnectMethod, setPendingDraftConnectMethod] = useState(null);
  const [draftConnectRowId, setDraftConnectRowId] = useState(null);
  const [, setDraftCsvFilesByRowId] = useState({});
  const draftCsvTargetRowIdRef = useRef(null);
  const draftCsvFilePickerRef = useRef(null);
  const connectSnapshotRef = useRef(null);

  useEffect(() => {
    draftRowsRef.current = Array.isArray(draftAccountSetupRows)
      ? draftAccountSetupRows
      : [];
  }, [draftAccountSetupRows]);

  const refreshPlaidItemList = async (id) => {
    if (!id) return null;

    try {
      const items = await fetchPlaiditems(id);
      setPlaidItems(Array.isArray(items) ? items : []);
      return items;
    } catch (err) {
      console.error("Error fetching plaid items", err);
      return null;
    }
  };

  useEffect(() => {
    seededFromExistingRef.current = false;
    setDraftAccountSetupRows([]);
    setDraftCsvFilesByRowId({});
    setPendingDraftConnectMethod(null);
    setDraftConnectRowId(null);
    connectSnapshotRef.current = null;
  }, [customerId]);

  useEffect(() => {
    if (!customerId) {
      setPlaidItems([]);
      setCheckingPlaid(false);
      return;
    }

    let active = true;
    void (async () => {
      setCheckingPlaid(true);
      try {
        const items = await fetchPlaiditems(customerId);
        if (!active) return;
        setPlaidItems(Array.isArray(items) ? items : []);
      } catch (err) {
        console.error("❌ Error fetching plaid items:", err);
        if (!active) return;
        setPlaidItems([]);
      } finally {
        if (active) setCheckingPlaid(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [customerId]);

  const connectedAccountRows = useMemo(
    () =>
      (Array.isArray(plaidItems) ? plaidItems : [])
        .filter((item) => item?.product === "transactions")
        .flatMap((item) =>
          (item?.bankaccount_set || []).map((account) => ({
            plaidItemId: item?.id,
            bankName: item?.name,
            csvFileName: item?.import_file_name || null,
            accountId: account?.id,
            accountName: account?.name,
            accountType: account?.type,
            accountSubtype: account?.subtype,
            isCsv: Boolean(item?.is_fake_import),
            amountSignConvention: normalizeConvention(account?.amount_sign_convention),
          }))
        ),
    [plaidItems]
  );

  useEffect(() => {
    const hasConnectedAccounts = connectedAccountRows.length > 0;

    if (
      hasConnectedAccounts &&
      draftAccountSetupRows.length === 0 &&
      !seededFromExistingRef.current
    ) {
      setDraftAccountSetupRows(
        connectedAccountRows.map((r) => ({
          id: `existing-${r.accountId}`,
          bankName: r.bankName || "",
          accountName: r.accountName || "",
          typeKey:
            deriveAccountTypeKeyFromApi({
              accountType: r.accountType,
              accountSubtype: r.accountSubtype,
            }) || "checking",
          isConnected: true,
          connectionMethod: r.isCsv ? "csv" : "plaid",
          source: "existing",
          sourceAccountId: r.accountId,
          sourcePlaidItemId: r.plaidItemId,
          amountSignConvention: r.amountSignConvention,
        }))
      );
      seededFromExistingRef.current = true;
    }

    if (!hasConnectedAccounts && seededFromExistingRef.current) {
      const onlySeeded =
        (Array.isArray(draftAccountSetupRows) ? draftAccountSetupRows : [])
          .length > 0 &&
        (Array.isArray(draftAccountSetupRows) ? draftAccountSetupRows : []).every(
          (r) => r?.source === "existing"
        );

      if (onlySeeded) {
        setDraftAccountSetupRows([]);
      }
    }
  }, [connectedAccountRows, draftAccountSetupRows]);

  useEffect(() => {
    if (connectedAccountRows.length === 0) return;

    const byAccountId = new Map(
      connectedAccountRows
        .filter((r) => r?.accountId)
        .map((r) => [
          r.accountId,
          deriveAccountTypeKeyFromApi({
            accountType: r.accountType,
            accountSubtype: r.accountSubtype,
          }),
        ])
    );

    setDraftAccountSetupRows((prev) => {
      const rows = Array.isArray(prev) ? prev : [];
      let changed = false;

      const next = rows.map((row) => {
        const sourceAccountId = row?.sourceAccountId || null;
        if (!sourceAccountId) return row;

        const nextTypeKey = byAccountId.get(sourceAccountId) || "";
        if (!nextTypeKey) return row;
        if (row?.typeKey === nextTypeKey) return row;
        changed = true;
        return { ...row, typeKey: nextTypeKey };
      });

      return changed ? next : prev;
    });
  }, [connectedAccountRows]);

  const persistRowEdits = async ({ nextRow, prevRow }) => {
    if (!customerId) return;
    const bankAccountId = nextRow?.sourceAccountId || null;
    const plaidItemId = nextRow?.sourcePlaidItemId || null;

    if (!bankAccountId || !plaidItemId) return;
    if (!nextRow?.isConnected) return;

    const prevTypeKey = prevRow?.typeKey || "";
    const nextTypeKey = nextRow?.typeKey || "";
    const prevBankName = (prevRow?.bankName || "").trim();
    const nextBankName = (nextRow?.bankName || "").trim();

    const typeChanged = Boolean(nextTypeKey) && prevTypeKey !== nextTypeKey;
    const bankNameChanged = Boolean(nextBankName) && prevBankName !== nextBankName;

    if (!typeChanged && !bankNameChanged) return;

    try {
      if (bankNameChanged) {
        await updatePlaidItem(customerId, plaidItemId, { name: nextBankName });
      }

      if (typeChanged) {
        const { type, subtype } = plaidTypeSubtypeFromTypeKey(nextTypeKey);
        await updateBankAccount(bankAccountId, {
          ...(type ? { type } : {}),
          ...(subtype ? { subtype } : {}),
        });
      }

      await refreshPlaidItemList(customerId);
    } catch (err) {
      console.error("Failed saving bank account edits:", err);

      // Recovery: refetch so UI reflects backend truth next time user returns.
      await refreshPlaidItemList(customerId);
    }
  };

  const setRowsWithPersistence = (nextOrUpdater) => {
    const prev = Array.isArray(draftRowsRef.current) ? draftRowsRef.current : [];
    const next =
      typeof nextOrUpdater === "function" ? nextOrUpdater(prev) : nextOrUpdater;

    setDraftAccountSetupRows(next);
    draftRowsRef.current = Array.isArray(next) ? next : [];

    const prevById = new Map(
      (Array.isArray(prev) ? prev : []).map((r) => [r?.id, r])
    );
    (Array.isArray(next) ? next : []).forEach((row) => {
      const rowId = row?.id || null;
      if (!rowId) return;
      const prevRow = prevById.get(rowId);
      if (!prevRow) return;

      if (!row?.sourceAccountId || !row?.sourcePlaidItemId) return;

      void persistRowEdits({ nextRow: row, prevRow });
    });
  };

  const openConventionDialog = (row) => {
    if (!row?.sourceAccountId) return;
    if (row?.connectionMethod !== "csv") return;
    if (!row?.isConnected) return;

    setConventionDialogRow(row);
    setConventionDialogValue(normalizeConvention(row?.amountSignConvention));
    setConventionDialogOpen(true);
  };

  const closeConventionDialog = () => {
    setConventionDialogOpen(false);
    setConventionDialogRow(null);
  };

  const confirmConventionFlip = async () => {
    if (!customerId) return;
    const row = conventionDialogRow;
    const accountId = row?.sourceAccountId || null;
    if (!accountId) return;

    const currentConvention = normalizeConvention(row?.amountSignConvention);
    const nextConvention = normalizeConvention(conventionDialogValue);
    if (currentConvention === nextConvention) return;

    setConventionPendingByAccountId((prev) => ({
      ...(prev || {}),
      [accountId]: true,
    }));

    try {
      await flipBankAccountSignConvention(accountId, {
        new_convention: nextConvention,
        rerun_categorization: true,
      });

      setDraftAccountSetupRows((prev) =>
        (Array.isArray(prev) ? prev : []).map((r) =>
          r?.sourceAccountId === accountId
            ? { ...r, amountSignConvention: nextConvention }
            : r
        )
      );

      closeConventionDialog();
      await refreshPlaidItemList(customerId);
    } catch (err) {
      console.error("Failed flipping sign convention:", err);
    } finally {
      setConventionPendingByAccountId((prev) => ({
        ...(prev || {}),
        [accountId]: false,
      }));
    }
  };

  const dialogAccountId = conventionDialogRow?.sourceAccountId || null;
  const dialogBusy = Boolean(
    dialogAccountId && conventionPendingByAccountId?.[dialogAccountId]
  );
  const dialogCurrent = normalizeConvention(conventionDialogRow?.amountSignConvention);
  const dialogNext = normalizeConvention(conventionDialogValue);
  const dialogSelectionValid = dialogCurrent !== dialogNext;

  return (
    <Card
      sx={{
        borderRadius: "12px",
        border: "1px solid #E0E0E0",
        boxShadow: "0px 1px 4px 0px rgba(0,0,0,0.04)",
        overflow: "hidden",
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          px: "24px",
          py: "14px",
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          backgroundColor: "#fff",
        }}
      >
        <AccountBalanceOutlinedIcon sx={{ color: "#536DEC", fontSize: 20 }} />
        <Typography
          sx={{
            fontFamily: "Rethink Sans",
            fontWeight: 600,
            fontSize: "18px",
            lineHeight: "175%",
            letterSpacing: "0.15px",
            color: "rgba(0,0,0,0.87)",
            flex: 1,
          }}
        >
          Bank Connections
        </Typography>
        <Chip
          label={`${connectedCount}/${totalCount} Connected`}
          sx={{
            backgroundColor: "rgba(0,0,0,0.08)",
            fontFamily: "Rethink Sans",
            fontWeight: 500,
            fontSize: "13px",
            letterSpacing: "0.16px",
            px: "4px",
          }}
        />
      </Box>

      <Box
        sx={{
          px: "24px",
          pt: "20px",
          pb: "24px",
          backgroundColor: "#fff",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        {!customerId ? (
          <Typography
            sx={{
              fontFamily: "Rethink Sans",
              fontWeight: 400,
              fontSize: "14px",
              lineHeight: "150%",
              letterSpacing: "0.098px",
              color: "rgba(0,0,0,0.6)",
            }}
          >
            No active customer selected.
          </Typography>
        ) : (
          <>
            {/* Hidden triggers so the table dropdown can reuse existing Plaid/CSV flows */}
            <Box
              sx={{
                position: "fixed",
                left: "-10000px",
                top: "-10000px",
                width: "1px",
                height: "1px",
                overflow: "hidden",
                opacity: 0,
                pointerEvents: "none",
              }}
            >
              <LinkPlaidButton
                customer_id={customerId}
                bankLinked={false}
                checkingPlaid={checkingPlaid}
                setStepperStep={() => {}}
                labelWhenUnlinked="Connect Via Plaid"
                labelWhenLinked="Connected"
                disableAutoAdvance={true}
                startIcon={<LinkIcon />}
                buttonSx={{}}
                autoOpenKey={
                  pendingDraftConnectMethod?.method === "plaid"
                    ? pendingDraftConnectMethod.key
                    : null
                }
                onExchangeStart={() => setPlaidLinking(true)}
                onBankLinkedChange={(linked) => {
                  if (!linked) return;
                  const snapshot = connectSnapshotRef.current;
                  const previousIds = new Set(snapshot?.plaidItemIds || []);
                  setPendingDraftConnectMethod(null);

                  void (async () => {
                    try {
                      const nextItems = await refreshPlaidItemList(customerId);
                      const newItem = (Array.isArray(nextItems) ? nextItems : []).find(
                        (item) => item?.id && !previousIds.has(item.id)
                      );

                      const plaidItemId = newItem?.id || null;
                      const targetRowId = snapshot?.rowId || draftConnectRowId;
                      const bankAccounts = newItem?.bankaccount_set || [];
                      const desiredBankName = (snapshot?.bankName || "").trim();

                      if (targetRowId) {
                        setDraftAccountSetupRows((prev) => {
                          const list = Array.isArray(prev) ? prev : [];
                          if (bankAccounts.length > 0) {
                            // Replace the single draft row with one connected row
                            // per account that Plaid actually returned.
                            const without = list.filter((r) => r?.id !== targetRowId);
                            const newRows = bankAccounts.map((account) => ({
                              id: `plaid_${plaidItemId}_${account.id}`,
                              bankName: desiredBankName || newItem?.name || "",
                              accountName: account.official_name || account.name || "",
                              typeKey: deriveAccountTypeKeyFromApi({ accountType: account.type, accountSubtype: account.subtype }) || "checking",
                              isConnected: true,
                              connectionMethod: "plaid",
                              sourcePlaidItemId: plaidItemId,
                              sourceAccountId: account.id,
                            }));
                            return [...without, ...newRows];
                          }
                          // Fallback: if Plaid returned no account details,
                          // mark the original draft row as connected.
                          return list.map((r) =>
                            r?.id === targetRowId
                              ? {
                                  ...r,
                                  isConnected: true,
                                  connectionMethod: "plaid",
                                  sourcePlaidItemId: plaidItemId,
                                }
                              : r
                          );
                        });
                      }

                      if (plaidItemId && desiredBankName) {
                        await updatePlaidItem(customerId, plaidItemId, {
                          name: desiredBankName,
                        });
                        await refreshPlaidItemList(customerId);
                      }
                    } catch (err) {
                      console.error("Failed processing Plaid connection:", err);
                    } finally {
                      connectSnapshotRef.current = null;
                      setDraftConnectRowId(null);
                      setPlaidLinking(false);
                    }
                  })();
                }}
              />

              <input
                ref={draftCsvFilePickerRef}
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  const targetRowId = draftCsvTargetRowIdRef.current;
                  if (file && targetRowId) {
                    setDraftCsvFilesByRowId((prev) => ({
                      ...(prev || {}),
                      [targetRowId]: file,
                    }));
                  }
                  draftCsvTargetRowIdRef.current = null;
                  setPendingDraftConnectMethod(null);

                  if (!file || !targetRowId) return;

                  const targetRow = (
                    Array.isArray(draftAccountSetupRows) ? draftAccountSetupRows : []
                  ).find((r) => r?.id === targetRowId);
                  const bankName = (targetRow?.bankName || "").trim();
                  const accountName = (targetRow?.accountName || "").trim();

                  if (!bankName || !accountName) {
                    return;
                  }

                  void (async () => {
                    setCreatingCsvAccount(true);
                    try {
                      const snapshot = connectSnapshotRef.current;
                      const previousIds = new Set(snapshot?.plaidItemIds || []);

                      const fd = new FormData();
                      fd.append("bank_name", bankName);
                      fd.append("account_name", accountName);
                      fd.append("file", file);
                      fd.append("customer_id", customerId);
                      const sign = csvAmountSignConventionFromTypeKey(targetRow?.typeKey);
                      fd.append("csv_amount_sign_convention", sign);

                      const { type: accountType, subtype: accountSubtype } =
                        plaidTypeSubtypeFromTypeKey(targetRow?.typeKey || "checking");
                      fd.append("account_type", accountType);
                      fd.append("account_subtype", accountSubtype);

                      const res = await fakePlaidImport(fd);
                      if (res?.status !== 202) {
                        throw new Error("Unexpected response from server.");
                      }

                      setDraftAccountSetupRows((prev) =>
                        (Array.isArray(prev) ? prev : []).map((r) =>
                          r?.id === targetRowId
                            ? { ...r, isConnected: true, connectionMethod: "csv" }
                            : r
                        )
                      );

                      const nextItems = await refreshPlaidItemList(customerId);
                      const newItem = (Array.isArray(nextItems) ? nextItems : []).find(
                        (item) => item?.id && !previousIds.has(item.id)
                      );

                      const createdAccount =
                        (newItem?.bankaccount_set || []).find(
                          (a) => (a?.name || "").trim() === accountName
                        ) || (newItem?.bankaccount_set || [])[0];
                      const conventionFromApi =
                        (createdAccount?.amount_sign_convention || "").trim() || null;

                      setDraftAccountSetupRows((prev) =>
                        (Array.isArray(prev) ? prev : []).map((r) =>
                          r?.id === targetRowId
                            ? {
                                ...r,
                                sourcePlaidItemId: newItem?.id || null,
                                sourceAccountId: createdAccount?.id || r?.sourceAccountId,
                                amountSignConvention:
                                  conventionFromApi
                                    ? normalizeConvention(conventionFromApi)
                                    : sign,
                              }
                            : r
                        )
                      );

                      connectSnapshotRef.current = null;
                    } catch (err) {
                      console.error("Draft CSV import failed:", err);
                    } finally {
                      setCreatingCsvAccount(false);
                    }
                  })();
                }}
              />
            </Box>

            <Box sx={evenlySpacedTableSx}>
              {(plaidLinking || creatingCsvAccount) ? (
                <ConnectedAccountsSkeletonComponent
                  rows={draftAccountSetupRows.length || 2}
                />
              ) : (
              <ConnectedAccountsStepComponent
                rows={draftAccountSetupRows}
                setRows={setRowsWithPersistence}
                renderConnectedRowExtras={(row) => {
                  if (!row?.isConnected) return null;
                  if (row?.connectionMethod !== "csv") return null;
                  if (!row?.sourceAccountId) return null;

                  const accountId = row.sourceAccountId;
                  const busy = Boolean(conventionPendingByAccountId?.[accountId]);
                  return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        component="span"
                        sx={{
                          fontFamily: "Rethink Sans",
                          fontWeight: 500,
                          fontSize: "0.8125rem",
                          lineHeight: "18px",
                          letterSpacing: "0.16px",
                          color: "rgba(0,0,0,0.6)",
                        }}
                      >
                        Sign: {conventionLabel(row?.amountSignConvention)}
                      </Typography>
                      <Button
                        size="small"
                        variant="text"
                        disabled={busy}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openConventionDialog(row);
                        }}
                        sx={{
                          fontFamily: "Rethink Sans",
                          textTransform: "none",
                          minWidth: "auto",
                          px: 1,
                        }}
                      >
                        {busy ? "Updating…" : "Change"}
                      </Button>
                    </Box>
                  );
                }}
                onDeleteRow={(row) => {
                  const rowId = row?.id || null;
                  if (!rowId) return;

                  setDraftAccountSetupRows((prev) =>
                    (Array.isArray(prev) ? prev : []).filter((r) => r?.id !== rowId)
                  );

                  if (row?.sourcePlaidItemId && customerId) {
                    setPlaidItems((prev) =>
                      (Array.isArray(prev) ? prev : []).filter(
                        (p) => p?.id !== row.sourcePlaidItemId
                      )
                    );

                    void (async () => {
                      try {
                        await deletePlaidItem(customerId, row.sourcePlaidItemId);
                        await refreshPlaidItemList(customerId);
                      } catch (err) {
                        console.error("Failed deleting plaid item:", err);
                        await refreshPlaidItemList(customerId);

                        setDraftAccountSetupRows((prev) => {
                          const list = Array.isArray(prev) ? prev : [];
                          if (list.some((r) => r?.id === rowId)) return list;
                          return [...list, row];
                        });
                      }
                    })();
                  }
                }}
                onDisconnectRow={(row) => {
                  const rowId = row?.id || null;
                  if (!rowId) return;
                  const previousConnectionMethod = row?.connectionMethod || null;
                  const previousSourcePlaidItemId = row?.sourcePlaidItemId || null;

                  setDraftAccountSetupRows((prev) =>
                    (Array.isArray(prev) ? prev : []).map((r) =>
                      r?.id === rowId
                        ? {
                            ...r,
                            isConnected: false,
                            connectionMethod: null,
                            sourcePlaidItemId: null,
                          }
                        : r
                    )
                  );

                  if (previousSourcePlaidItemId && customerId) {
                    void (async () => {
                      try {
                        await deletePlaidItem(customerId, previousSourcePlaidItemId);
                        await refreshPlaidItemList(customerId);
                      } catch (err) {
                        console.error("Failed removing connection:", err);
                        await refreshPlaidItemList(customerId);

                        setDraftAccountSetupRows((prev) =>
                          (Array.isArray(prev) ? prev : []).map((r) =>
                            r?.id === rowId
                              ? {
                                  ...r,
                                  isConnected: true,
                                  connectionMethod: previousConnectionMethod,
                                  sourcePlaidItemId: previousSourcePlaidItemId,
                                }
                              : r
                          )
                        );
                      }
                    })();
                  }
                }}
                onConnectData={(row, options) => {
                  if (options?.method !== "plaid" && options?.method !== "csv")
                    return;

                  const rowId = row?.id || null;
                  setDraftConnectRowId(rowId);

                  if (options.method === "csv") {
                    draftCsvTargetRowIdRef.current = rowId;
                  }

                  connectSnapshotRef.current = {
                    rowId,
                    method: options.method,
                    bankName: (row?.bankName || "").trim(),
                    accountName: (row?.accountName || "").trim(),
                    plaidItemIds: (Array.isArray(plaidItems) ? plaidItems : [])
                      .map((p) => p?.id)
                      .filter(Boolean),
                  };

                  setPendingDraftConnectMethod({ method: options.method, key: Date.now() });

                  if (options.method === "csv") {
                    try {
                      if (draftCsvFilePickerRef.current) {
                        draftCsvFilePickerRef.current.value = "";
                        draftCsvFilePickerRef.current.click();
                      }
                    } catch (err) {
                      console.warn("Failed opening CSV file picker:", err);
                    }
                  }
                }}
              />
              )}
            </Box>

            <Dialog
              open={conventionDialogOpen}
              onClose={dialogBusy ? undefined : closeConventionDialog}
              maxWidth="sm"
              fullWidth
              sx={{ zIndex: 99999 }}
            >
              <DialogTitle sx={{ fontFamily: "Rethink Sans", fontWeight: 600 }}>
                Change sign convention
              </DialogTitle>
              <DialogContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "Rethink Sans",
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "150%",
                      letterSpacing: "0.098px",
                      color: "rgba(0,0,0,0.6)",
                    }}
                  >
                    This will invert the sign of all amounts (and balances) for this account.
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: "Rethink Sans",
                      fontWeight: 400,
                      fontSize: "14px",
                      lineHeight: "150%",
                      letterSpacing: "0.098px",
                      color: "rgba(0,0,0,0.6)",
                    }}
                  >
                    Categorization will automatically re-run after this change.
                  </Typography>

                  <FormControl>
                    <FormLabel sx={{ fontFamily: "Rethink Sans" }}>
                      New convention
                    </FormLabel>
                    <RadioGroup
                      value={conventionDialogValue}
                      onChange={(e) => setConventionDialogValue(e.target.value)}
                    >
                      <FormControlLabel
                        value="lucro"
                        control={<Radio />}
                        label="Lucro (debit/normal)"
                        disabled={dialogBusy}
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontFamily: "Rethink Sans",
                          },
                        }}
                      />
                      <FormControlLabel
                        value="plaid"
                        control={<Radio />}
                        label="Plaid (credit/inverted)"
                        disabled={dialogBusy}
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontFamily: "Rethink Sans",
                          },
                        }}
                      />
                    </RadioGroup>
                  </FormControl>

                </Box>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button
                  onClick={closeConventionDialog}
                  disabled={dialogBusy}
                  variant="outlined"
                  sx={{ fontFamily: "Rethink Sans", textTransform: "none" }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmConventionFlip}
                  variant="contained"
                  disabled={!dialogSelectionValid || dialogBusy}
                  startIcon={dialogBusy ? <CircularProgress size={16} /> : null}
                  sx={{ fontFamily: "Rethink Sans", textTransform: "none" }}
                >
                  {dialogBusy ? "Updating…" : "Confirm"}
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Box>
    </Card>
  );
}

